import React, { createContext, useContext } from 'react';
import { useSelector } from 'react-redux';
import { IUser, ISpace, IBoundToSpace, IHotelFilter, IHotel } from '../types';
import { UserFullFragment, SpaceForDropdownFragment, HotelForDropdownFragment } from '__generated__/graphql';

export const initialSpaceId = (user: IUser, filter?: IHotelFilter | null, defaultSpaceId?: number | null) => {
  if (filter && filter.spaceId && canEditSpaceId(user, filter.spaceId)) return filter.spaceId;
  else if (user && user.space && user.space.id) return user.space.id;
  else if (defaultSpaceId !== undefined && defaultSpaceId !== null) return defaultSpaceId;
  else if (user && user.spaces && user.spaces.length > 0) return user.spaces[0].id;
  return null;
};
export const initialSpace = (user: IUser, filter?: IHotelFilter | null, defaultSpaceId?: number | null) => {
  const spaceId = initialSpaceId(user, filter, defaultSpaceId);
  if (spaceId) return user.spaces.find(s => s.id === spaceId) || null;
  else return null;
};

export const user2IUser = (user: UserFullFragment, spaces?: SpaceForDropdownFragment[], hotels?: HotelForDropdownFragment[]): IUser => {
  const memberships = user.memberships.filter(m => user.space && user.space.id !== m.space?.id);
  const result: IUser = {
    id: user.id,
    name: user.name,
    email: user.email,
    isAdmin: !!user.isAdmin,
    hasLister: !!user.hasLister,
    hasWidget: !!user.hasWidget,
    isRoot: !user.space && user.name === 'root',
    isSeminargo: !user.space,
    isSingleSpace: false,
    isSingleAdminSpace: false,
    space: user.space || null,
    memberships: memberships,
    spaces: (spaces || []) as ISpace[],
    hotels: (hotels || []) as IHotel[],
  };
  if (user.space) {
    if (user.space.activeSEMPlan && user.space.activeSEMPlan.semPlan.flags.multiTenancy) {
      result.isSingleSpace = (spaces || []).filter(s => user.space!.id !== s.id).length === 0;
      result.isSingleAdminSpace = (spaces || []).filter(s => user.space!.id !== s.id && canEditAdminSpace(result, s)).length === 0;
    } else {
      result.isSingleSpace = true;
      result.isSingleAdminSpace = canEditAdminSpace(result, user.space);
    }
  }
  return result;
};

/** Guest offer deeplink: synthetic user without Redux login (see OfferForm provider). */
export const GuestOfferUserContext = createContext<IUser | null>(null);

export const userSelector = () => {
  const guestUser = useContext(GuestOfferUserContext);
  const reduxUser = useSelector<any, IUser | null>(state => state.user.value || null);
  return guestUser ?? reduxUser;
};

/** Synthetic backoffice user for guest offer deeplink sessions (hotel-scoped permissions). */
export const buildGuestOfferUser = (space: ISpace | null | undefined): IUser => ({
  id: -1,
  email: '',
  name: 'guest',
  isAdmin: true,
  hasLister: false,
  hasWidget: true,
  isRoot: false,
  isSeminargo: false,
  isSingleSpace: true,
  isSingleAdminSpace: true,
  space: space ?? null,
  memberships: [],
  spaces: space ? [space] : [],
  hotels: [],
});

export const canEditRecord = (user: IUser | undefined | null, record: IBoundToSpace) => {
  if (!user) return false;
  if (user.isRoot) return true;
  if (user.isSeminargo) return true;
  if (!record.space || !record.space.id) return false;

  const space = user.spaces.find(s => s.id === record.space!.id);
  if (!space) return false;

  if (user.space && space.id === user.space.id) return true;
  if (user.memberships.findIndex(m => m.space?.id === space.id) >= 0) return true;
  if (space.parent) return canEditAdminSpace(user, space.parent);
  return false;
};
export const canEditSpace = (user: IUser | undefined | null, space: ISpace) => {
  return canEditRecord(user, { space });
};
export const canEditSpaceId = (user: IUser | undefined | null, spaceId: number) => {
  return canEditRecord(user, { space: user?.spaces.find(s => s.id === spaceId) });
};

export const hasAnyAdminSpace = (user: IUser | undefined | null): boolean => {
  if (!user) return false;
  if (user.isRoot) return true;
  if (user.isSeminargo) return true;
  if (user.isAdmin) return true;
  if (user.memberships.filter(m => m.isAdmin).length > 0) return true;
  return false;
};

export const canEditAdminRecord = (user: IUser | undefined | null, record: IBoundToSpace): boolean => {
  if (!user) return false;
  if (user.isRoot) return true;
  if (user.isSeminargo) return true;
  if (!record.space || !record.space.id) return false;
  if (user.space && record.space!.id === user.space.id && user.isAdmin) return true;

  const space = user.spaces.find(s => s.id === record.space!.id);
  if (!space) return false;

  if (user.memberships.findIndex(m => m.isAdmin && m.space?.id === space.id) >= 0) return true;
  if (space.parent) return canEditAdminSpace(user, space.parent);
  return false;
};
export const canEditAdminSpace = (user: IUser | undefined | null, space: ISpace) => {
  return canEditAdminRecord(user, { space });
};
export const canEditAdminSpaceId = (user: IUser | undefined | null, spaceId: number) => {
  if (!user) return false;
  if (user.isRoot || user.isSeminargo) return true;
  const space = user.spaces.find(s => s.id === spaceId) ?? (user.space?.id === spaceId ? user.space : null);
  if (!space) return false;
  return canEditAdminRecord(user, { space });
};

const _flattenChildren = (space: ISpace): number[] => {
  return (space.children || []).reduce<number[]>((acc, child) => acc.concat(_flattenChildren(child)), [space.id]);
};
const _flattenParents = (space: ISpace): number[] => {
  return [...(space.parent ? _flattenParents(space.parent) : []), space.id];
};

export const getSuccessorSpaceIds = (spaces: ISpace[], spaceId: number): number[] => {
  const space = spaces.find(s => s.id === spaceId);
  if (!space) return [spaceId];
  return _flattenChildren(space);
};

export const filterForSelectableRecords = <TList extends IBoundToSpace>(
  user: IUser | undefined | null,
  list: TList[],
  spaceId: number,
  includeSuccessors: boolean,
  includeAncestors: boolean,
): TList[] => {
  if (!user) return [];
  // For Seminargo users without loaded spaces: if ancestors are needed but we can't
  // determine them (no space hierarchy), return all items. Otherwise filter by exact spaceId.
  if (user.isSeminargo && user.spaces.length === 0) {
    if (includeAncestors || includeSuccessors) {
      // Can't determine hierarchy without user.spaces, return all
      return list;
    }
    // Filter by exact spaceId only
    return list.filter(l => l.space && l.space.id === spaceId);
  }
  const validSpaceIds = [spaceId];
  const space = user.spaces.find(s => s.id === spaceId);
  if (space && includeSuccessors) {
    validSpaceIds.push(..._flattenChildren(space));
  }
  if (space && includeAncestors) {
    validSpaceIds.push(..._flattenParents(space));
  }
  return list.filter(l => l.space && validSpaceIds.indexOf(l.space.id) >= 0);
};

export const sha256Hex = async (input: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}
